export class CircularTimer {
    head = document.head
    timer = 3.5
    shadowTimer = 0.6
    timerFinished = false
    containerId: string
    timerContainer: HTMLElement | null
    timerEndCallback: ((animationEvent: any) => void) | null = null
    fullTimerElements = [".right-container:after", ".left-container:after"]
    halfTimerElements = [".right-container", ".left-container"]
    delayElement: string
    shadowCount = 0
    vendorPrefixes = ["webkit", "moz", "ms", "o"]
    cssAttributeTemplates = {
        color: "border-color",
        delay: "animation-delay",
        duration: "animation-duration",
        transition: "transition",
        transform: "transform",
    }

    constructor(timerId: string) {
        this.containerId = timerId
        this.timerContainer = document.querySelector(`#${this.containerId}`)
        this.delayElement = this.halfTimerElements[1]
    }

    startTimer(endCallback: (animationEvent: any) => void) {
        this.addTimersToStyles()

        if (endCallback instanceof Function) {
            const animationEnded = this.timerAnimationEnded.bind(this, endCallback)
            this.timerEndCallback = animationEnded

            this.timerContainer?.querySelector(this.delayElement)?.addEventListener("animationend", animationEnded)
        }

        this.timerFinished = false
        this.timerContainer?.classList.add("animate")
    }

    stopTimer() {
        const shadowId = `shadow-${++this.shadowCount}`
        const shadowStyle = this.addShadowStyles(shadowId)

        if (null === shadowStyle || !this.timerContainer?.classList.contains("animate")) {
            return
        }

        this.timerContainer.classList.remove("animate")

        if (null !== this.timerEndCallback) {
            this.timerContainer
                ?.querySelector(this.delayElement)
                ?.removeEventListener("animationend", this.timerEndCallback)
            this.timerEndCallback = null
        }

        const shadowContainer = this.timerContainer.cloneNode(true) as HTMLElement

        shadowContainer.classList.add("timer-container-shadow")
        shadowContainer.classList.remove("timer-container")

        shadowContainer.setAttribute("id", shadowId)
        this.timerContainer?.parentNode?.appendChild(shadowContainer)

        shadowContainer.addEventListener(
            "transitionend",
            this.removeShadowContainer.bind(this, shadowContainer, shadowStyle),
        )

        window.setTimeout(() => {
            shadowContainer.classList.add("animate")
        }, 10)
    }

    animationDuration(durationInSeconds: string) {
        this.timer = Number.parseFloat(durationInSeconds)

        if (Number.isNaN(this.timer)) {
            throw new Error("Die übergebene Dauer ist kein gültiger Wert")
        }
    }

    fadeDuration(durationInSeconds: string) {
        this.shadowTimer = Number.parseFloat(durationInSeconds)

        if (Number.isNaN(this.shadowTimer)) {
            throw new Error("Die übergebene Dauer ist kein gültiger Wert")
        }
    }

    timerAnimationEnded(endCallback: (animationEvent: any, x: any) => void, animationEvent: any) {
        if (null !== this.timerEndCallback) {
            animationEvent.target.removeEventListener("animationend", this.timerEndCallback)
            this.timerEndCallback = null
        }

        if (this.timerFinished) {
            return
        }

        this.timerFinished = true
        endCallback(animationEvent, this)

        this.stopTimer()
    }

    addTimersToStyles() {
        const fullTimer = this.timer * 1000
        const halfTimer = fullTimer / 2
        const timerStyle = document.createElement("style")
        const timerId = `${this.timerContainer?.getAttribute("id")}-style`
        const oldTimerStyle = document.querySelector(`#${timerId}`)
        const styleContainerId = `#${this.containerId} `

        if (null !== oldTimerStyle) {
            oldTimerStyle.parentNode?.removeChild(oldTimerStyle)
        }

        timerStyle.setAttribute("type", "text/css")
        timerStyle.setAttribute("id", timerId)

        timerStyle.innerText += `${
            styleContainerId + this.fullTimerElements.join(",")
        }{${this.generateValueAttributes(this.cssAttributeTemplates.duration, `${fullTimer}ms`, true)}}`

        timerStyle.innerText += `${
            styleContainerId + this.halfTimerElements.join(",")
        }{${this.generateValueAttributes(this.cssAttributeTemplates.duration, `${halfTimer}ms`, true)}}`

        timerStyle.innerText += `${
            styleContainerId + this.delayElement
        }{${this.generateValueAttributes(this.cssAttributeTemplates.delay, `${halfTimer}ms`, true)}}`

        this.head.appendChild(timerStyle)
    }

    addShadowStyles(shadowCount: string) {
        if (!this.timerContainer) {
            return null
        }

        const fullTimer = this.shadowTimer * 1000
        const rightElement = this.timerContainer.querySelector(".right-container") as HTMLElement
        const leftElement = this.timerContainer.querySelector(".left-container") as HTMLElement
        const rightStyle = window.getComputedStyle(rightElement)
        const leftStyle = window.getComputedStyle(leftElement)
        const ringStyle = window.getComputedStyle(rightElement, ":after")
        const rightMatrix = this.findMatrixData(rightStyle)
        const leftMatrix = this.findMatrixData(leftStyle)
        const borderColor = ringStyle.getPropertyValue("border-top-color")
        const rightContainer = `#${shadowCount} .right-container`
        const leftContainer = `#${shadowCount} .left-container`
        const colorElements = [`${rightContainer}:after`, `${leftContainer}:after`]
        const shadowStyle = document.createElement("style")

        shadowStyle.setAttribute("type", "text/css")

        shadowStyle.innerText += `#${shadowCount}{${this.generateValueAttributes(
            this.cssAttributeTemplates.transition,
            `opacity ${fullTimer}ms`,
            true,
        )}}`

        shadowStyle.innerText += `${rightContainer}{${this.generateValueAttributes(this.cssAttributeTemplates.transform, rightMatrix, true)}}`

        shadowStyle.innerText += `${leftContainer}{${this.generateValueAttributes(this.cssAttributeTemplates.transform, leftMatrix, true)}}`

        shadowStyle.innerText += `${colorElements.join(",")}{${this.generateValueAttributes(this.cssAttributeTemplates.color, borderColor)}}`

        this.head.appendChild(shadowStyle)

        return shadowStyle
    }

    removeShadowContainer(shadowContainer: HTMLElement, shadowStyle: HTMLElement) {
        shadowContainer.parentNode?.removeChild(shadowContainer)
        document.head.removeChild(shadowStyle)
    }

    findMatrixData(computedStyle: CSSStyleDeclaration) {
        return (
            computedStyle.getPropertyValue("-webkit-transform") ||
            computedStyle.getPropertyValue("-moz-transform") ||
            computedStyle.getPropertyValue("-ms-transform") ||
            computedStyle.getPropertyValue("-o-transform") ||
            computedStyle.getPropertyValue("transform") ||
            ""
        )
    }

    generateValueAttributes(attribute: string, value: string, usePrefixes = false) {
        let attributes = ""
        const prefixCount = this.vendorPrefixes.length
        let index = 0

        if (true === usePrefixes) {
            for (; index < prefixCount; index++) {
                attributes += `-${this.vendorPrefixes[index]}-${attribute}:${value};`
            }
        }

        attributes += `${attribute}:${value};`

        return attributes
    }
}
